import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Descriptions, Button, Spin, List, message } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import documentService, { DocumentRecord } from '../services/document';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getFileIconByType } from '../config/file-type-icons';
import mediaService, { MediaFile } from '../services/media';

// 文档详情页面：展示单条文档记录的详细信息
const DocumentDetail: React.FC = () => {
  useDocumentTitle('文件详情');
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState<DocumentRecord | null>(null);
  const [mediaItems, setMediaItems] = useState<Array<{ id: number; name: string; file_size?: number; mime_type?: string; file_type?: string; source: 'doc' | 'media'; url?: string; is_image?: boolean }>>([]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const res = await documentService.getFile(Number(id));
        if (res.success && res.data) {
          const d = res.data;
          setDoc(d);
          // 构建媒体列表：优先使用后端提供的 media_files_info；若无，则从 files 字段解析媒体库ID并拉取详情
          const info = (d as any).media_files_info as any[] | undefined;
          if (Array.isArray(info) && info.length > 0) {
            const mapped = info.map((m: any) => {
              const idNum = Number(m.id);
              const mime = m.mime_type || m.file_type;
              const isImg = typeof mime === 'string' && mime.toLowerCase().startsWith('image');

              // 获取精简的文件类型（用于显示图标）
              const simpleFileType = mediaService.getFileTypeIcon({
                original_name: m.name,
                filename: m.name,
                mime_type: mime
              } as MediaFile);

              return {
                id: idNum,
                name: m.name || m.file_name || `文件${idNum}`,
                file_size: m.file_size || m.size,
                mime_type: mime,
                file_type: simpleFileType,
                source: 'doc' as const,
                url: m.url || (m.path ? mediaService.getMediaUrl({ path: m.path } as MediaFile) : undefined), // Use direct URL or storage path
                is_image: isImg,
              };
            });
            setMediaItems(mapped);
          } else if (d.files) {
            try {
              const ids: number[] = Array.isArray(d.files) ? (d.files as any) : JSON.parse(String(d.files));
              if (Array.isArray(ids) && ids.length > 0) {
                const results = await Promise.all(ids.map(id => mediaService.getMedia(Number(id))));
                const ok: MediaFile[] = results.filter(r => r.success && r.data).map(r => r.data!) as MediaFile[];
                const mapped = ok.map(m => ({
                  id: m.id,
                  name: m.title || m.original_name || m.filename,
                  file_size: m.file_size,
                  mime_type: m.mime_type,
                  file_type: mediaService.getFileTypeIcon(m),
                  source: 'media' as const,
                  url: mediaService.getMediaUrl(m),
                  is_image: !!m.mime_type && m.mime_type.toLowerCase().startsWith('image'),
                }));
                setMediaItems(mapped);
              } else {
                setMediaItems([]);
              }
            } catch {
              setMediaItems([]);
            }
          } else {
            setMediaItems([]);
          }
        } else {
          message.error(res.message || '无法获取文档详情');
        }
      } catch (e) {
        message.error('获取文档详情时发生错误');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);





  const handlePreviewMedia = (item: any) => {
    if (item.url) {
      window.open(item.url, '_blank');
    } else if (item.id) {
       // Fallback: try to fetch media info if URL is missing
       mediaService.getMedia(item.id).then(res => {
        if (res.success && res.data) {
          const url = mediaService.getMediaUrl(res.data);
          window.open(url, '_blank');
        }
      });
    }
  };

  const hasField = (fieldKey: keyof DocumentRecord | 'media_files_info'): boolean => {
    return !!doc && Object.prototype.hasOwnProperty.call(doc, fieldKey);
  };

  const detailItems = doc ? [
    { key: 'name', label: '文件名称', content: doc.name || (doc.media_files_info && doc.media_files_info[0]?.name) || '-' },
    { key: 'nation', label: '适用地区', content: doc.nation || '-' },
    { key: 'upload_at', label: '上传时间', content: doc.upload_at ? dayjs(doc.upload_at).format('YYYY-MM-DD HH:mm') : '-' },
    { key: 'updated_at', label: '最后更新', content: doc.updated_at ? dayjs(doc.updated_at).format('YYYY-MM-DD HH:mm') : '-' },
    { key: 'remark', label: '文件说明', content: <pre>{doc.remark || '-'}</pre> },
  ].filter(item => hasField(item.key as keyof DocumentRecord)) : [];

  const canViewMediaFiles = hasField('files') || hasField('media_files_info');

  // 媒体列表展示在下方卡片中

  return (
    <div style={{ padding: 24 }}>
      {/* 保持详情页表头（th/label）不换行，避免中文标签换行导致布局不齐 */}
      <style>{`.doc-desc .ant-descriptions-item-label { white-space: nowrap; }`}</style>
      <Card title="文档详情" extra={<Button onClick={() => navigate(-1)}>返回</Button>}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Descriptions className="doc-desc" bordered size="middle" column={1}>
            {detailItems.map(item => (
              <Descriptions.Item key={item.key} label={item.label}>
                {item.content}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Card>

      {/* 媒体文件列表 */}
      {canViewMediaFiles && (
        <Card title="媒体文件" style={{ marginTop: 16 }}>
          {mediaItems.length === 0 ? (
            <div style={{ color: '#999' }}>暂无媒体文件</div>
          ) : (
            <List
              grid={{ gutter: 16, column: 6 }}
              dataSource={mediaItems}
              renderItem={(m: any) => (
                <List.Item>
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: '100%', height: 120, backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {m.is_image && m.url ? (
                        <img src={m.url} alt={m.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ padding: 12, textAlign: 'center' }}>
                          <div style={{ fontSize: 28, marginBottom: 4 }}>
                            {getFileIconByType(m.file_type)}
                          </div>
                          <div style={{ fontSize: 12, color: '#999' }}>{m.file_type || '文件'}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '8px 8px 4px 8px' }} title={m.name}>
                      <div style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                      <div style={{ color: '#999', fontSize: 12 }}>{typeof m.file_size === 'number' ? `${(m.file_size/1024).toFixed(2)} KB` : ''}</div>
                    </div>
                    <div style={{ padding: '4px 8px 8px 8px', display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handlePreviewMedia(m)}
                        style={{ padding: 0 }}
                      >
                        预览
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        style={{ padding: 0 }}
                        onClick={() => mediaService.downloadMedia(m.id, m.name).catch(() => message.error('下载失败'))}
                      >
                        下载
                      </Button>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default DocumentDetail;
